import React, { useEffect, useState } from "react";
import { BarChart3, Download, Mail, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import CTAButton from "../components/common/CTAButton";
import ForecastPlaceholder from "../components/common/ForecastPlaceholder";
import ForecastStrip from "../components/common/ForecastStrip";
import OutlookPanel from "../components/common/OutlookPanel";
import { useAuth } from "../contexts/AuthContext";
import { emailCheckInResult, saveCheckInResult } from "../services/checkInApi";
import { generateQuitPlanFromCheckIn } from "../services/quitPlanApi";
import "./CheckInPage.css";

const SECTION_OPTIONS = [
  { key: "summary", label: "Risk summary" },
  { key: "triggers", label: "Key triggers" },
  { key: "actions", label: "Important actions" },
  { key: "steps", label: "Steps to follow" },
  { key: "domain_breakdown", label: "Section breakdown" },
  { key: "question_breakdown", label: "Question-by-question detail" },
];

const CheckInResultPage = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { user, loading, isLoggedIn } = useAuth();

  const assessment = state?.assessment;
  const saved = state?.saved;
  const answers = state?.answers || {};

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [selectedSections, setSelectedSections] = useState([
    "summary",
    "triggers",
    "actions",
    "steps",
  ]);

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showToast = (message, type = "success") => {
    setToast({ open: true, message, type });
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!toast.open) return;

    const timer = setTimeout(() => {
      closeToast();
    }, 3500);

    return () => clearTimeout(timer);
  }, [toast.open]);

  useEffect(() => {
    if (saved) {
      showToast("Your check-in result has been saved.");
    }
  }, [saved]);

  const openEmailModal = () => {
    setIsEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    if (sending) return;
    setIsEmailModalOpen(false);
  };

  const handleSectionToggle = (sectionKey) => {
    setSelectedSections((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((item) => item !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      showToast("Please enter an email address.", "error");
      return;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(trimmedEmail)) {
      showToast("Please enter a valid email address.", "error");
      return;
    }

    if (selectedSections.length === 0) {
      showToast("Please select at least one section.", "error");
      return;
    }

    try {
      setSending(true);

      const result = await emailCheckInResult(
        trimmedEmail,
        assessment,
        selectedSections
      );

      showToast(
        result.message || "Your result has been emailed successfully.",
        "success"
      );
      setEmail("");
      setIsEmailModalOpen(false);
    } catch (err) {
      showToast(err.message || "Failed to send result email.", "error");
    } finally {
      setSending(false);
    }
  };

  const handleSaveResult = async () => {
    try {
      setSaving(true);

      const result = await saveCheckInResult({
        total_score: assessment.total_score,
        risk_level: assessment.risk_level,
        answers_json: answers,
        result_json: assessment,
      });

      showToast(
        result.message || "Check-in result saved successfully.",
        "success"
      );
    } catch (err) {
      showToast(err.message || "Failed to save check-in result.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateQuitPlan = async () => {
    try {
      setGeneratingPlan(true);

      await generateQuitPlanFromCheckIn({
        assessment,
        answers,
      });

      navigate("/quit-plan");
    } catch (err) {
      showToast(err.message || "Failed to generate quit plan.", "error");
    } finally {
      setGeneratingPlan(false);
    }
  };

  if (loading) {
    return (
      <main className="checkin-page" aria-label="Relapse risk result">
        <div className="checkin-page__container">
          <p className="checkin-page__status">Loading...</p>
        </div>
      </main>
    );
  }

  if (!assessment) {
    return (
      <main className="checkin-page" aria-label="Relapse risk result">
        <div className="checkin-page__container">
          <section className="checkin-page__section-card">
            <h1 className="checkin-page__heading">No result found</h1>
            <p className="checkin-page__message">
              Please complete a check-in first to view your relapse risk result.
            </p>
            <div className="checkin-page__actions">
              <CTAButton
                label="Start a new check-in"
                to="/check-in"
                ariaLabel="Go to check-in page"
              />
              <CTAButton
                label="Back to Home"
                to="/"
                ariaLabel="Return to homepage"
              />
            </div>
          </section>
        </div>
      </main>
    );
  }

  return (
    <>
      <main className="checkin-page" aria-label="Relapse risk result">
        <div className="checkin-page__container">
          <header className="checkin-page__header checkin-page__header--result">
            <div className="checkin-page__header-top">
              <div className="checkin-page__header-action-left">
                {isLoggedIn ? (
                  <>
                    <button
                      type="button"
                      className="checkin-page__ghost-action"
                      onClick={handleSaveResult}
                      disabled={saving}
                      aria-label="Save check-in result"
                    >
                      <Download size={18} aria-hidden="true" />
                      <span>{saving ? "Saving..." : "Save Result"}</span>
                    </button>
                    <button
                      type="button"
                      className="checkin-page__ghost-action"
                      onClick={handleGenerateQuitPlan}
                      disabled={generatingPlan}
                      aria-label="Generate quit plan from this check-in"
                    >
                      <BarChart3 size={18} aria-hidden="true" />
                      <span>
                        {generatingPlan ? "Generating..." : "Generate Quit Plan"}
                      </span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="checkin-page__ghost-action"
                    onClick={openEmailModal}
                    aria-label="Send result via email"
                  >
                    <Mail size={18} aria-hidden="true" />
                    <span>Send Email</span>
                  </button>
                )}
              </div>
            </div>

            <div className="checkin-page__icon" aria-hidden="true">
              <BarChart3 />
            </div>

            <h1 className="checkin-page__heading">Your Check-In Result</h1>

            <p className="checkin-page__message">
              This shows your current relapse risk, the main triggers, and
              suggested actions.
            </p>

            {isLoggedIn && user && (
              <p className="checkin-page__message">
                Signed in as {user.full_name || user.email}
              </p>
            )}
          </header>

          <div className="checkin-page__result-column">
            <section className="checkin-page__section-card">
              <h2 className="checkin-page__section-title">Risk summary</h2>
              <div
                className={`checkin-page__badge checkin-page__badge--${assessment.risk_level.toLowerCase()}`}
              >
                {assessment.risk_level} Risk
              </div>
              <p className="checkin-page__score">
                Total score: <strong>{assessment.total_score}</strong> / 100
              </p>
            </section>

            {assessment.outlook && (
              <OutlookPanel
                fourWeek={assessment.outlook.fourWeek}
                fiftyTwoWeek={assessment.outlook.fiftyTwoWeek}
                sourceLabel={assessment.outlook.sourceLabel}
                sourceUrl={assessment.outlook.sourceUrl}
                riskLevel={assessment.risk_level}
                topFactors={assessment.top_factors}
              />
            )}

            {isLoggedIn &&
              (assessment.forecast_available ? (
                <ForecastStrip forecast={assessment.forecast} />
              ) : (
                <ForecastPlaceholder
                  checkinCount={assessment.checkin_count || 0}
                />
              ))}

            <section className="checkin-page__section-card">
              <h2 className="checkin-page__section-title">Key triggers</h2>
              {assessment.triggers?.length > 0 ? (
                <ul className="checkin-page__list">
                  {assessment.triggers.map((t) => (
                    <li
                      key={t.trigger_code}
                      className="checkin-page__list-item"
                    >
                      <strong>{t.trigger_label}</strong>
                      {t.description && <p>{t.description}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="checkin-page__empty">
                  No strong triggers were detected from this check-in.
                </p>
              )}
            </section>

            <section className="checkin-page__section-card">
              <h2 className="checkin-page__section-title">Important actions</h2>
              {assessment.actions?.length > 0 ? (
                <ul className="checkin-page__list">
                  {assessment.actions.map((a) => (
                    <li key={a.code} className="checkin-page__list-item">
                      <strong>{a.title}</strong>
                      {a.body && <p>{a.body}</p>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="checkin-page__empty">
                  No actions available for this result.
                </p>
              )}
            </section>

            <section className="checkin-page__section-card">
              <h2 className="checkin-page__section-title">Steps to follow</h2>
              {assessment.steps?.length > 0 ? (
                <ol className="checkin-page__list checkin-page__list--ordered">
                  {assessment.steps.map((s) => (
                    <li key={s.code} className="checkin-page__list-item">
                      <strong>{s.title}</strong>
                      {s.body && <p>{s.body}</p>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="checkin-page__empty">
                  No follow-up steps available for this result.
                </p>
              )}
            </section>

            <section className="checkin-page__section-card">
              <h2 className="checkin-page__section-title">
                How your score was calculated
              </h2>

              {assessment.domain_breakdown?.length > 0 && (
                <>
                  <h3 className="checkin-page__subheading">
                    Contribution from each area
                  </h3>
                  <table className="checkin-page__table">
                    <thead>
                      <tr>
                        <th>Domain</th>
                        <th>Score</th>
                        <th>Max</th>
                        <th>Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessment.domain_breakdown.map((d) => (
                        <tr key={d.domain_code}>
                          <td>{d.domain_label}</td>
                          <td>{d.raw_score}</td>
                          <td>{d.max_score}</td>
                          <td>{d.weight}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {assessment.breakdown?.length > 0 && (
                <>
                  <h3 className="checkin-page__subheading">
                    Question-by-question detail
                  </h3>
                  <div className="checkin-page__table-wrapper">
                    <table className="checkin-page__table">
                      <thead>
                        <tr>
                          <th>Question</th>
                          <th>Your answer</th>
                          <th>Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assessment.breakdown.map((item) => (
                          <tr key={item.question_code}>
                            <td>{item.question_text}</td>
                            <td>{item.matched_label || "-"}</td>
                            <td>{item.score_value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </section>
          </div>

          <div className="checkin-page__actions">
            <CTAButton
              label="Do another check-in"
              to="/check-in"
              ariaLabel="Start another check-in"
            />
            <CTAButton
              label="Back to Home"
              to="/"
              ariaLabel="Return to homepage"
            />
          </div>
        </div>
      </main>

      {!isLoggedIn && isEmailModalOpen && (
        <div
          className="checkin-page__modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="email-result-title"
        >
          <div className="checkin-page__modal">
            <div className="checkin-page__modal-header">
              <h2
                id="email-result-title"
                className="checkin-page__section-title"
              >
                Send Result via Email
              </h2>
              <button
                type="button"
                className="checkin-page__modal-close"
                onClick={closeEmailModal}
                aria-label="Close email popup"
              >
                ×
              </button>
            </div>

            <p className="checkin-page__message">
              Enter your email and choose which sections to include.
            </p>

            <form
              className="checkin-page__email-form"
              onSubmit={handleSendEmail}
            >
              <input
                type="email"
                className="checkin-page__input"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <div className="checkin-page__checkbox-group">
                {SECTION_OPTIONS.map((section) => (
                  <label
                    key={section.key}
                    className="checkin-page__checkbox-item"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSections.includes(section.key)}
                      onChange={() => handleSectionToggle(section.key)}
                    />
                    <span>{section.label}</span>
                  </label>
                ))}
              </div>

              <div className="checkin-page__modal-actions">
                <CTAButton
                  label={sending ? "Sending..." : "Send Email"}
                  type="submit"
                  ariaLabel="Send result email"
                  disabled={sending}
                />
                <CTAButton
                  label="Cancel"
                  onClick={closeEmailModal}
                  ariaLabel="Cancel sending email"
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {toast.open && (
        <div
          className={`checkin-page__toast checkin-page__toast--${toast.type}`}
          role="status"
          aria-live="polite"
        >
          <span className="checkin-page__toast-message">{toast.message}</span>
          <button
            type="button"
            className="checkin-page__toast-close"
            onClick={closeToast}
            aria-label="Close notification"
          >
            <X size={16} />
          </button>
        </div>
      )}
    </>
  );
};

export default CheckInResultPage;
